// useLayerThumbnails — レイヤーのサムネ (layer-thumbnails-2026-10-13.md) の配線 (両レイアウト共通)。
//
// - engine ごとに `LayerThumbnailHub` を 1 つ作る (パネルは hub に canvas を登録するだけ)。
// - ユーザー設定 (既定 ON) を engine の ON/OFF に反映する。OFF = 番号だけ + エンジンの処理も止まる。
// - パン中は `setLayerThumbnailsSuspended(true)`。自分のストローク中は hub が貼らず、エンジン側も
//   自分で止まる。判定式はプレビューのサスペンドと共有 (`isLocalInteractionActive`)。
// - 'stopped' (遅い端末での自動停止、そのタブの間は戻らない) を画面に伝える。
// - 「見えているか」はパネルの登録で決まる (hub が `setLayerThumbnailsVisible` を送る)。
//   Classic の LayerPopup は Tab で隠すと unmount、スマホのシートは閉じると unmount、
//   レイアウト切替でも unmount されるので、取り違えない。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  CanvasDrawingEngine,
  LayerThumbnailState,
} from '../../../infrastructure/drawing/CanvasDrawingEngine.js';
import { createLayerThumbnailPreferenceStore } from '../../../infrastructure/layerThumbnailPreference.js';
import { isLocalInteractionActive } from '../classic/previewOverlay.js';
import { LayerThumbnailHub } from './layerThumbnailHub.js';

export interface UseLayerThumbnailsResult {
  /** パネルが canvas を登録する窓口。engine 未準備は null (= 番号だけ)。 */
  readonly hub: LayerThumbnailHub | null;
  /** ユーザー設定 (既定 ON)。 */
  readonly enabled: boolean;
  readonly setEnabled: (next: boolean) => void;
  /** エンジンが遅い端末で自動停止した (そのタブの間は設定を ON にしても戻らない)。 */
  readonly stopped: boolean;
}

export const useLayerThumbnails = (
  engine: CanvasDrawingEngine | null,
  isStrokeActive: () => boolean,
  panActive: boolean,
): UseLayerThumbnailsResult => {
  const store = useMemo(() => createLayerThumbnailPreferenceStore(), []);
  const [enabled, setEnabledState] = useState<boolean>(() => store.get());
  const [state, setState] = useState<LayerThumbnailState>('off');
  const [hub, setHub] = useState<LayerThumbnailHub | null>(null);

  const panRef = useRef(panActive);
  panRef.current = panActive;
  const strokeRef = useRef(isStrokeActive);
  strokeRef.current = isStrokeActive;

  useEffect(() => {
    if (engine === null) {
      setHub(null);
      setState('off');
      return;
    }
    const h = new LayerThumbnailHub({
      engine,
      isDrawing: () => isLocalInteractionActive(strokeRef.current(), panRef.current),
    });
    const unsub = h.subscribeState(setState);
    setHub(h);
    return () => {
      unsub();
      h.dispose();
    };
  }, [engine]);

  useEffect(() => {
    if (engine === null || hub === null) return;
    engine.setLayerThumbnailsEnabled(enabled, { visible: hub.hasSurface() });
  }, [engine, hub, enabled]);

  const sentSuspendedRef = useRef(false);
  useEffect(() => {
    if (hub === null) return;
    if (!panActive && !sentSuspendedRef.current) return;
    sentSuspendedRef.current = panActive;
    hub.setSuspended(panActive);
  }, [hub, panActive]);

  const setEnabled = useCallback(
    (next: boolean): void => {
      setEnabledState(next);
      store.set(next);
    },
    [store],
  );

  return { hub, enabled, setEnabled, stopped: state === 'stopped' };
};
